'use client'

import { Button, Input, LuneTheme, useDevice } from '@lune-fe/lune-ui-lib'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import Box from '@mui/material/Box'
import Text from '@mui/material/Typography'
import { FC, useEffect, useState } from 'react'
import { useDebounce } from 'use-debounce'

import TwoColumnsWithBackgroundImage from '@/components/TwoColumnsWithBackgroundImage'
import { useNewsletter } from '@/hooks/useNewsLetter'

// eslint-disable-next-line complexity
const BusinessSpendForm: FC<{ hubspotFormId: string }> = ({ hubspotFormId }) => {
    const { palette } = LuneTheme
    const { smScreen, xsScreen, mdScreen } = useDevice()
    const { isValidEmail, toHubspot } = useNewsletter(hubspotFormId, [
        `Planet and profit: The secret advantage of business spend`,
        'Spend management is sitting on a bedrock of opportunity, and they don’t even know it. Worldwide, over 50,000 companies are grappling with mandatory emissions reporting and growing consumer demand for sustainable choices. Learn how spend management platforms can offer a quick and easy solution.',
    ])

    const [name, setName] = useState<string>('')
    const [lastName, setLastName] = useState<string>('')
    const [email, setEmail] = useState<string>('')
    const [company, setCompany] = useState<string>('')
    const [nameError, setNameError] = useState<boolean>(false)
    const [lastNameError, setLastNameError] = useState<boolean>(false)
    const [emailError, setEmailError] = useState<boolean>(false)
    const [companyError, setCompanyError] = useState<boolean>(false)
    const [success, setSuccess] = useState<boolean>()
    const [debouncedEmail] = useDebounce(email, 1000)

    useEffect(() => {
        if (isValidEmail(email) || email === '') {
            setEmailError(false)
        } else {
            setEmailError(true)
        }
    }, [debouncedEmail, email, isValidEmail])

    useEffect(() => {
        setNameError(false)
    }, [name])

    useEffect(() => {
        setLastNameError(false)
    }, [lastName])

    useEffect(() => {
        setCompanyError(false)
    }, [company])

    const onSubmit = async () => {
        const validName = name.length
        const validLastName = lastName.length
        const validEmail = isValidEmail(email)
        const validCompany = company.length

        if (!validName || !validEmail || !validCompany) {
            !validName && setNameError(true)
            !validLastName && setLastNameError(true)
            !validEmail && setEmailError(true)
            !validCompany && setCompanyError(true)

            return
        }

        const data = await toHubspot({
            firstname: name,
            lastname: lastName,
            email,
            company,
            legalConsent: 'gatedContent',
        })
        if (data.status === 'error') {
            setSuccess(false)
        } else {
            setSuccess(true)
            setName('')
            setLastName('')
            setEmail('')
            setCompany('')

            gtag('event', 'request_demo', {})
        }
    }

    return (
        <TwoColumnsWithBackgroundImage
            backgroundImage={'/images/planet-and-profit-desktop.png'}
            mobileBackgroundImage={'/images/planet-and-profit-mobile.png'}
            rightSide={<Box sx={{ height: smScreen ? '268px' : xsScreen ? '268px' : 'auto' }} />}
            reversedOnMobile
            leftSide={
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        flexDirection: 'column',
                        gap: mdScreen ? 3 : 4,
                    }}
                >
                    {success ? (
                        <Box
                            sx={{
                                height: mdScreen ? '508px' : '572px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: mdScreen ? 4 : 9,
                                alignItems: 'flex-start',
                                justifyContent: 'center',
                            }}
                        >
                            <Text variant={mdScreen ? 'h5' : 'h4'}>
                                {`Thanks, your free guide is on its way to your inbox!`}
                            </Text>
                        </Box>
                    ) : (
                        <>
                            <Text variant={mdScreen ? 'h5' : 'h4'}>
                                Planet and profit: The secret advantage of business spend
                            </Text>
                            <Text variant={mdScreen ? 'body2' : 'body1'}>
                                Spend management is sitting on a bedrock of opportunity, and they
                                don’t even know it. Worldwide, over 50,000 companies are grappling
                                with mandatory emissions reporting. Learn how spend management
                                platforms can offer a quick and easy solution.
                            </Text>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    justifyContent: 'space-between',
                                    flexDirection: 'column',
                                    gap: 1,
                                    width: '100%',
                                    '.MuiInputBase-root ': {
                                        width: '100%',
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'stretch',
                                        justifyContent: 'space-between',
                                        flexDirection: 'row',
                                        gap: 1,
                                        width: '100%',
                                    }}
                                >
                                    <Input
                                        placeholder={'Name'}
                                        error={nameError}
                                        value={name}
                                        sx={{ backgroundColor: palette.White }}
                                        onChange={(e) => setName(e.target.value)}
                                    />
                                    <Input
                                        placeholder={'Surname'}
                                        error={lastNameError}
                                        value={lastName}
                                        sx={{ backgroundColor: palette.White }}
                                        onChange={(e) => setLastName(e.target.value)}
                                    />
                                </Box>
                                <Input
                                    placeholder={'Company name'}
                                    error={companyError}
                                    sx={{ backgroundColor: palette.White }}
                                    value={company}
                                    onChange={(e) => setCompany(e.target.value)}
                                />
                                <Input
                                    placeholder={'Work email'}
                                    error={emailError}
                                    sx={{ backgroundColor: palette.White }}
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                />
                            </Box>
                            <Button
                                onClick={onSubmit}
                                leftIcon={<FileDownloadOutlinedIcon />}
                                sx={{ width: { xs: '100%', sm: 'auto' } }}
                            >
                                Discover the secret
                            </Button>
                        </>
                    )}
                </Box>
            }
        />
    )
}

export default BusinessSpendForm
